import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { FONT, NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";
import { optionHintSx } from "./SettingsSection";
import { CustomerApproverSummary } from "./authorizationSettingsStore";

interface ApproverSummaryListProps {
  summary: readonly CustomerApproverSummary[];
  onSelect: (customerId: string) => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function describeApproverCounts({ adminCount, hrCount }: CustomerApproverSummary): string {
  const parts = [
    adminCount > 0 ? plural(adminCount, "admin", "admins") : null,
    hrCount > 0 ? plural(hrCount, "HR contact", "HR contacts") : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

/** Customers that already have approvers saved. Picking one opens it in the editor above. */
export default function ApproverSummaryList({ summary, onSelect }: ApproverSummaryListProps) {
  if (summary.length === 0) {
    return <Typography sx={optionHintSx}>No customer has approvers yet. Select a customer to add their admins and HR contacts.</Typography>;
  }

  return (
    <Box>
      <Typography component="h3" sx={{ fontFamily: FONT.sans, fontSize: "13px", fontWeight: 700, color: NEUTRAL.textSecondary, mb: 1 }}>
        Customers with approvers ({summary.length})
      </Typography>
      <Box
        component="ul"
        aria-label="Customers with approvers"
        sx={{ m: 0, p: 0, listStyle: "none", border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.card}px`, overflow: "hidden" }}
      >
        {summary.map((item, index) => (
          <Box component="li" key={item.customerId} sx={{ borderTop: index === 0 ? "none" : `1px solid ${NEUTRAL.border}` }}>
            <ButtonBase
              onClick={() => onSelect(item.customerId)}
              aria-label={`${item.customerName}: ${describeApproverCounts(item)}`}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.25,
                textAlign: "left",
                transition: "background-color 150ms ease",
                "&:hover": { background: NEUTRAL.hover },
                "&.Mui-focusVisible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
              }}
            >
              <Typography sx={{ fontFamily: FONT.sans, fontSize: "14px", fontWeight: 600, color: NEUTRAL.text }}>{item.customerName}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <Typography sx={{ fontFamily: FONT.sans, fontSize: "13px", color: NEUTRAL.textSecondary }}>
                  {describeApproverCounts(item)}
                </Typography>
                <ChevronRightOutlinedIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} aria-hidden />
              </Box>
            </ButtonBase>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
